/* eslint-disable @typescript-eslint/no-empty-function */

/* eslint-disable @typescript-eslint/no-unused-vars */
var ConnectElementCustomMethodConfig = {
  "account-onboarding": {
    setFullTermsOfServiceUrl: function setFullTermsOfServiceUrl(_termOfServiceUrl) {},
    setRecipientTermsOfServiceUrl: function setRecipientTermsOfServiceUrl(_recipientTermsOfServiceUrl) {},
    setPrivacyPolicyUrl: function setPrivacyPolicyUrl(_privacyPolicyUrl) {},
    setSkipTermsOfServiceCollection: function setSkipTermsOfServiceCollection(_skipTermsOfServiceCollection) {},
    setCollectionOptions: function setCollectionOptions(_collectionOptions) {},
    setOnExit: function setOnExit(_listener) {}
  },
  "account-management": {
    setCollectionOptions: function setCollectionOptions(_collectionOptions) {}
  },
  "notification-banner": {
    setCollectionOptions: function setCollectionOptions(_collectionOptions) {},
    setOnNotificationsChange: function setOnNotificationsChange(_listener) {}
  },
  "payment-details": {
    setPayment: function setPayment(_payment) {},
    setOnClose: function setOnClose(_listener) {}
  }
};
var componentNameMapping = {
  "account-onboarding": "stripe-connect-account-onboarding",
  payments: "stripe-connect-payments",
  "payment-details": "stripe-connect-payment-details",
  payouts: "stripe-connect-payouts",
  "payouts-list": "stripe-connect-payouts-list",
  balances: "stripe-connect-balances",
  "account-management": "stripe-connect-account-management",
  "notification-banner": "stripe-connect-notification-banner",
  documents: "stripe-connect-documents"
};
var EXISTING_SCRIPT_MESSAGE = "loadConnect was called but an existing Connect.js script already exists in the document; existing script parameters will be used";
var V0_URL = "https://connect-js.stripe.com/v0.1/connect.js";
var V1_URL = "https://connect-js.stripe.com/v1.0/connect.js";

var findScript = function findScript() {
  return document.querySelectorAll("script[src=\"".concat(V1_URL, "\"]"))[0] || document.querySelectorAll("script[src=\"".concat(V0_URL, "\"]"))[0] || null;
};

var injectScript = function injectScript() {
  var script = document.createElement("script");
  script.src = V1_URL;
  var head = document.head;

  if (!head) {
    throw new Error("Expected document.head not to be null. Connect.js requires a <head> element.");
  }

  document.head.appendChild(script);
  return script;
};

var stripePromise = null;

var loadScript = function loadScript() {
  // Ensure that we only attempt to load Connect.js at most once
  if (stripePromise !== null) {
    return stripePromise;
  }

  stripePromise = new Promise(function (resolve, reject) {
    if (typeof window === "undefined") {
      reject("ConnectJS won't load when rendering code in the server - it can only be loaded on a browser. This error is expected when loading ConnectJS in SSR environments, like NextJS. It will have no impact in the UI, however if you wish to avoid it, you can switch to the `pure` version of the connect.js loader: https://github.com/stripe/connect-js#importing-loadconnect-without-side-effects.");
      return;
    }

    if (window.StripeConnect) {
      console.warn(EXISTING_SCRIPT_MESSAGE);
    }

    if (window.StripeConnect) {
      var wrapper = createWrapper(window.StripeConnect);
      resolve(wrapper);
      return;
    }

    try {
      var script = findScript();

      if (script) {
        console.warn(EXISTING_SCRIPT_MESSAGE);
      } else if (!script) {
        script = injectScript();
      }

      script.addEventListener("load", function () {
        if (window.StripeConnect) {
          var _wrapper = createWrapper(window.StripeConnect);

          resolve(_wrapper);
        } else {
          reject(new Error("Connect.js did not load the necessary objects"));
        }
      });
      script.addEventListener("error", function () {
        reject(new Error("Failed to load Connect.js"));
      });
    } catch (error) {
      reject(error);
    }
  });
  return stripePromise;
};

var hasCustomMethod = function hasCustomMethod(tagName) {
  return tagName in ConnectElementCustomMethodConfig;
};

var initStripeConnect = function initStripeConnect(stripePromise, initParams) {
  var _a;

  var eagerClientSecretPromise = function () {
    try {
      return initParams.fetchClientSecret();
    } catch (error) {
      return Promise.reject(error);
    }
  }();

  var metaOptions = (_a = initParams.metaOptions) !== null && _a !== void 0 ? _a : {};
  var stripeConnectInstance = stripePromise.then(function (wrapper) {
    return wrapper.initialize(Object.assign(Object.assign({}, initParams), {
      metaOptions: Object.assign(Object.assign({}, metaOptions), {
        eagerClientSecretPromise: eagerClientSecretPromise
      })
    }));
  });
  return {
    create: function create(tagName) {
      var htmlName = componentNameMapping[tagName];

      if (!htmlName) {
        htmlName = tagName;
      }

      var element = document.createElement(htmlName);

      if (hasCustomMethod(tagName)) {
        var methods = ConnectElementCustomMethodConfig[tagName];

        var _loop = function _loop(method) {
          element[method] = function (value) {
            var _this = this;

            stripeConnectInstance.then(function () {
              _this["".concat(method, "InternalOnly")](value);
            });
          };
        };

        for (var method in methods) {
          _loop(method);
        }
      }

      stripeConnectInstance.then(function (instance) {
        if (!element.isConnected && !element.setConnector) {
          // If the element is not connected to the DOM and the `setConnector` method is not
          // defined, this indicates the element was created before connect.js was loaded, and has
          // not been transformed into a custom element yet
          // To load the custom element code on it, we need to connect and disconnect it to the DOM
          // This isn't a problem, as the element will be invisible, and we know the element is already
          // not currently connected to the DOM
          var oldDisplay = element.style.display;
          element.style.display = "none";
          document.body.appendChild(element);
          document.body.removeChild(element);
          element.style.display = oldDisplay;
        }

        if (!element || !element.setConnector) {
          throw new Error("Element ".concat(tagName, " was not transformed into a custom element. Are you using a documented component? See https://docs.stripe.com/connect/supported-embedded-components for a list of supported components"));
        }

        element.setConnector(instance.connect);
      });
      return element;
    },
    update: function update(updateOptions) {
      stripeConnectInstance.then(function (instance) {
        instance.update(updateOptions);
      });
    },
    debugInstance: function debugInstance() {
      return stripeConnectInstance;
    },
    logout: function logout() {
      return stripeConnectInstance.then(function (instance) {
        return instance.logout();
      });
    }
  };
};

var createWrapper = function createWrapper(stripeConnect) {
  window.StripeConnect = window.StripeConnect || {};
  window.StripeConnect.optimizedLoading = true;
  var wrapper = {
    initialize: function initialize(params) {
      var _a;

      var metaOptions = (_a = params.metaOptions) !== null && _a !== void 0 ? _a : {};
      var stripeConnectInstance = stripeConnect.init(Object.assign(Object.assign({}, params), {
        metaOptions: Object.assign(Object.assign({}, metaOptions), {
          sdk: true,
          sdkOptions: {
            // This will be replaced by the npm package version when bundling
            sdkVersion: "3.3.10"
          }
        })
      }));
      return stripeConnectInstance;
    }
  };
  return wrapper;
}; // Execute our own script injection after a tick to give users time to do their
// own script injection.


var stripePromise$1 = Promise.resolve().then(function () {
  return loadScript();
});
var loadCalled = false;
stripePromise$1.catch(function (err) {
  if (!loadCalled) {
    console.warn(err);
  }
});

var loadConnectAndInitialize = function loadConnectAndInitialize(initParams) {
  loadCalled = true;
  return initStripeConnect(stripePromise$1, initParams);
};

export { loadConnectAndInitialize };